import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMixer } from '../context/MixerContext';
import './PerspectiveIndicator.css';

/**
 * A subtle, ambient indicator that shows the current "lens" through which
 * content is being viewed. Reinforces the mental model that the same work
 * looks different depending on perspective.
 */
export default function PerspectiveIndicator() {
  const { mixerState } = useMixer();
  
  // Calculate dominant perspective
  const techBiz = mixerState.technicalVsBusiness;
  const icLead = mixerState.icVsLeadership;
  const consEnt = mixerState.consultingVsEnterprise;
  
  // Determine the active quadrant/perspective
  const getPerspective = () => {
    const isTechnical = techBiz < 40;
    const isBusiness = techBiz > 60;
    const isIC = icLead < 40;
    const isLeadership = icLead > 60;
    
    if (isTechnical && isIC) return { label: 'Builder', sublabel: 'Deep in the code' };
    if (isTechnical && isLeadership) return { label: 'Architect', sublabel: 'Systems & structure' };
    if (isBusiness && isIC) return { label: 'Executor', sublabel: 'Delivering results' };
    if (isBusiness && isLeadership) return { label: 'Strategist', sublabel: 'Vision & direction' };
    
    return { label: 'Balanced', sublabel: 'Full spectrum view' };
  };
  
  const perspective = getPerspective();
  
  // Calculate ambient glow colors based on mixer state
  const techColor = `rgba(34, 211, 238, ${0.1 + (1 - techBiz / 100) * 0.15})`;
  const bizColor = `rgba(167, 139, 250, ${0.1 + (techBiz / 100) * 0.15})`;
  
  return (
    <div className="perspective-indicator">
      <div 
        className="perspective-indicator__glow perspective-indicator__glow--tech"
        style={{ opacity: (1 - techBiz / 100) * 0.6 }}
      />
      <div 
        className="perspective-indicator__glow perspective-indicator__glow--biz"
        style={{ opacity: (techBiz / 100) * 0.6 }}
      />
      
      <AnimatePresence mode="wait">
        <motion.div 
          key={perspective.label}
          className="perspective-indicator__label"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.3 }}
        >
          <span className="perspective-indicator__title">{perspective.label}</span>
          <span className="perspective-indicator__sublabel">{perspective.sublabel}</span>
        </motion.div>
      </AnimatePresence>
      
      <div className="perspective-indicator__matrix">
        <div 
          className="perspective-indicator__dot"
          style={{
            left: `${techBiz}%`,
            top: `${icLead}%`,
          }}
        />
        <div className="perspective-indicator__axes">
          <span className="perspective-indicator__axis-label perspective-indicator__axis-label--tl">Tech</span>
          <span className="perspective-indicator__axis-label perspective-indicator__axis-label--tr">Biz</span>
          <span className="perspective-indicator__axis-label perspective-indicator__axis-label--bl">IC</span>
          <span className="perspective-indicator__axis-label perspective-indicator__axis-label--br">Lead</span>
        </div>
      </div>
    </div>
  );
}
